import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  TextField, 
  Button, 
  Container, 
  Typography, 
  AppBar, 
  Toolbar, 
  Box, 
  CircularProgress, 
  Grid, 
  Link,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  IconButton
} from '@mui/material';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import Footer from './Footer';

function Login() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState({
    email: '',
    general: ''
  });

  const validateEmail = (email) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(String(email).toLowerCase());
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear errors when user types
    if (name === 'email') {
      setError(prev => ({
        ...prev,
        email: value && !validateEmail(value) ? 'Invalid email format' : ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;

    // Validate email
    if (!validateEmail(formData.email)) {
      setError(prev => ({
        ...prev,
        email: 'Invalid email format'
      }));
      return;
    }

    setDialogOpen(true);
  };

  const handleDialogClose = async (proceed) => {
    setDialogOpen(false);
    if (!proceed) return;

    setIsLoading(true);
    setError(prev => ({ ...prev, general: '' }));

    // Store in localStorage
    Object.entries(formData).forEach(([key, value]) => {
      localStorage.setItem(key, value);
    });

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/start-interview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: formData.email,
          first_name: formData.firstName,
          last_name: formData.lastName,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      localStorage.setItem('interview_id', data.interview_id);
      navigate('/interview');
    } catch (error) {
      setError(prev => ({
        ...prev,
        general: error.message || 'Failed to start interview. Please try again.'
      }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      bgcolor: 'background.default',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <AppBar position="fixed" className="mainAppBar">
        <Toolbar>
          <img 
            src="/aqiliSmartObject.png" 
            alt="Logo" 
            style={{ height: 40, marginRight: 16 }} 
          />
          <Typography variant="h4" sx={{ flexGrow: 1 }}>
            aqili
          </Typography>
          <Button color="inherit" href="/admin-login">
            Admin Login
          </Button>
        </Toolbar>
      </AppBar>

      <Container 
        component="main" 
        maxWidth="sm" 
        sx={{ 
          mt: 8,
          mb: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4,
            width: '100%',
            borderRadius: 2,
            marginTop: '100px'
          }}
        >
          <Typography 
            component="h1" 
            variant="h4" 
            align="center" 
            gutterBottom
            sx={{ 
              fontWeight: 600,
              color: 'primary.main'
            }}
          >
            Get Started with your Interview
          </Typography>
          
          <form onSubmit={handleSubmit} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              label="First Name"
              name="firstName"
              autoComplete="given-name"
              autoFocus
              value={formData.firstName}
              onChange={handleInputChange}
              variant="outlined"
              sx={{ mb: 2 }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Last Name"
              name="lastName"
              autoComplete="family-name"
              value={formData.lastName}
              onChange={handleInputChange}
              variant="outlined"
              sx={{ mb: 2 }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Email Address"
              name="email"
              autoComplete="email"
              value={formData.email}
              onChange={handleInputChange}
              error={!!error.email}
              helperText={error.email}
              variant="outlined"
              sx={{ mb: 3 }}
            />

            {error.general && (
              <Typography 
                color="error" 
                variant="body2" 
                sx={{ mb: 2 }}
              >
                {error.general}
              </Typography>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading}
              sx={{
                py: 1.5,
                fontSize: '1.1rem',
                position: 'relative'
              }}
            >
              {isLoading ? (
                <>
                  <CircularProgress 
                    size={24} 
                    sx={{ 
                      position: 'absolute',
                      left: '50%',
                      marginLeft: '-12px'
                    }}
                  />
                  <span style={{ visibility: 'hidden' }}>Start Interview</span>
                </>
              ) : (
                'Start Interview'
              )}
            </Button>
          </form>

          <Grid 
            container 
            spacing={3} 
            justifyContent="center" 
            sx={{ mt: 4 }}
          >
            <Grid item>
              <IconButton 
                component={Link}
                href="https://www.facebook.com/parkwaycorp/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: '#3b5998' }}
              >
                <FacebookIcon fontSize="large" />
              </IconButton>
            </Grid>
            <Grid item>
              <IconButton
                component={Link}
                href="https://www.linkedin.com/company/parkway-corporation"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: '#0077b5' }}
              >
                <LinkedInIcon fontSize="large" />
              </IconButton>
            </Grid>
            <Grid item>
              <IconButton
                component={Link}
                href="https://www.instagram.com/parkway_corp/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: '#E1306C' }}
              >
                <InstagramIcon fontSize="large" />
              </IconButton>
            </Grid>
          </Grid>
        </Paper>
      </Container>

      <Dialog
        open={dialogOpen}
        onClose={() => handleDialogClose(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Welcome to Your Interview
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            We would like to thank you for your interest in our parking attendant position. 
            The following chat conversation will be used to conduct your initial interview. 
            While this format uses AI to conduct the interview, all responses are reviewed and rated by our recruiters. 
            The AI is only used to facilitate asking questions. This format allows you to take the interview at your 
            convenience and pace and allows us to hire faster because we don't have to work around schedules to conduct interviews. 
            If you have any difficulties with the website, please contact Zechariah Dice at zdice@parkwaycorp.com.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleDialogClose(false)}>Cancel</Button>
          <Button onClick={() => handleDialogClose(true)} variant="contained">
            Continue
          </Button>
        </DialogActions>
      </Dialog>
      <Footer />
    </Box>
  );
}

export default Login;